import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, convertNumberToWords } from "../master/Master";

import { imgBaseUrl } from "../../api/api";
import { formatToDate } from "../master/FormatToDateTime";

import usePrivateLink from "../config/usePrivateLink";

import DistributionKitLayout from "./DistributionKitLayout";

export default function DistributionKitPaymentReceipt() {
  usePrivateLink();

  const navigate = useNavigate();
  const location = useLocation();

  const [setting, setSetting] = useState({});

  /* ============================================================
     LOAD SCHOOL SETTINGS
  ============================================================ */

  useEffect(() => {
    try {
      const savedSetting = localStorage.getItem("setting");

      if (savedSetting) {
        setSetting(JSON.parse(savedSetting));
      }
    } catch (error) {
      console.error("Unable to load school settings:", error);
    }
  }, []);

  /* ============================================================
     PAYMENT DATA
  ============================================================ */

  const payment = location?.state?.payment;

  /* ============================================================
     HELPERS
  ============================================================ */

  const getAssetUrl = (path) => {
    if (!path) {
      return "";
    }

    const base = String(imgBaseUrl || "").replace(/\/+$/, "");
    const file = String(path).replace(/^\/+/, "");

    return `${base}/${file}`;
  };

  const formatAmount = (value) => {
    return `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    try {
      return formatToDate(value);
    } catch {
      try {
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
          return value;
        }

        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        return value;
      }
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "—";
    }

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const getSchoolAddress = () => {
    return [
      setting?.village,
      setting?.district,
      setting?.state,
      setting?.pin_code,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const getSchoolMobile = () => {
    return [setting?.mobile, setting?.secondary_mobile]
      .filter(Boolean)
      .join(" / ");
  };

  const escapeHtml = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  /* ============================================================
     NO RECEIPT DATA
  ============================================================ */

  if (!payment) {
    return (
      <>
        <div className="layout-wrapper layout-content-navbar">
          <div className="layout-container">
            <Header />

            <div className="layout-page">
              <Nav />

              <div className="container-fluid flex-grow-1 container-p-y">
                <DistributionKitLayout />

                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center py-5">
                    <div
                      className="rounded-circle bg-label-warning d-flex align-items-center justify-content-center mx-auto mb-3"
                      style={{
                        width: 70,
                        height: 70,
                      }}
                    >
                      <i className="ri-file-warning-line fs-2 text-warning" />
                    </div>

                    <h5 className="fw-bold mb-2">Receipt Data Not Found</h5>

                    <p className="text-muted mb-4">
                      The payment receipt data is not available. Please return
                      to the distribution report.
                    </p>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate("/distribution-kits/report")}
                    >
                      <i className="ri-arrow-left-line me-1" />
                      Back to Distribution Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ============================================================
     PAYMENT OBJECTS
  ============================================================ */

  const transaction = payment?.transaction || {};
  const fee = payment?.fee || {};
  const paymentDetails = payment?.payment || {};

  /* ============================================================
     RECIPIENT
  ============================================================ */

  const isStudent = Boolean(fee?.student);
  const isStaff = Boolean(fee?.staff);

  const recipient = isStudent
    ? {
        name: fee.student.name || fee.student.student_name || "Student",

        type: "Student",

        registrationNo: fee.student.registration_no || "",

        fatherName: fee.student.father_name || "",

        mobile:
          fee.student.mobile ||
          fee.student.phone ||
          fee.student.mobile_no ||
          "",
      }
    : isStaff
      ? {
          name: fee.staff.name || fee.staff.staff_name || "Staff",

          type: "Staff",

          registrationNo: "",

          fatherName: "",

          mobile:
            fee.staff.mobile || fee.staff.phone || fee.staff.mobile_no || "",
        }
      : {
          name: "—",
          type: "—",
          registrationNo: "",
          fatherName: "",
          mobile: "",
        };

  /* ============================================================
     AMOUNTS
  ============================================================ */

  const kitAmount = Number(fee?.kit_amount || 0);

  const discountAmount = Number(fee?.discount_amount || 0);

  const finalAmount = Number(fee?.final_amount || 0);

  const previousPaidAmount = Number(
    paymentDetails?.previous_paid_amount ??
      transaction?.previous_paid_amount ??
      0,
  );

  const currentPayment = Number(
    paymentDetails?.amount ?? transaction?.amount ?? 0,
  );

  /*
   * Historical receipts may contain:
   *
   * paid_amount_after_payment
   *
   * If that is not available, calculate it.
   */
  const totalPaid = Number(
    paymentDetails?.paid_amount ??
      transaction?.paid_amount_after_payment ??
      previousPaidAmount + currentPayment,
  );

  /*
   * Historical receipts may contain:
   *
   * due_amount_after_payment
   *
   * Otherwise calculate from final amount.
   */
  const dueAmount = Math.max(
    0,
    Number(
      paymentDetails?.due_amount ??
        transaction?.due_amount_after_payment ??
        finalAmount - totalPaid,
    ),
  );

  const isFullyPaid = dueAmount <= 0;

  /* ============================================================
     SCHOOL DETAILS
  ============================================================ */

  const schoolName = setting?.name || "School Name";

  const schoolAddress = getSchoolAddress();

  const schoolMobile = getSchoolMobile();

  const schoolEmail = setting?.email || "";

  const logoUrl = getAssetUrl(setting?.logo_img);

  const headerLogoUrl = getAssetUrl(setting?.receipt_header_logo);

  /*
   * IMPORTANT:
   *
   * Do not check auto_signature here.
   *
   * Current setting:
   *
   * auto_signature = "No"
   * signature = "/images/Signature_1790247173.png"
   *
   * We want the configured signature to appear.
   */
  const signatureUrl = getAssetUrl(setting?.signature);

  const showSignature = Boolean(setting?.signature);

  /* ============================================================
     PAYMENT DETAILS
  ============================================================ */

  const paymentMethod = transaction?.payment_method || "—";

  const receiptNo =
    transaction?.receipt_no || `REP-${transaction?.id || fee?.id}`;

  const transactionId = transaction?.id || "—";

  /* ============================================================
     PRINT RECEIPT
  ============================================================ */

  const printReceipt = () => {
    const printWindow = window.open("", "_blank", "width=1000,height=900");

    if (!printWindow) {
      SweetAlertShow(
        "warning",
        "Popup Blocked",
        "Please allow popups to print the receipt.",
      );

      return;
    }

    /* ==========================================================
       SAFE VALUES FOR PRINT
    ========================================================== */

    const pSchoolName = escapeHtml(schoolName);

    const pSchoolAddress = escapeHtml(schoolAddress);

    const pSchoolMobile = escapeHtml(schoolMobile);

    const pSchoolEmail = escapeHtml(schoolEmail);

    const pReceiptNo = escapeHtml(receiptNo);

    const pRecipientName = escapeHtml(recipient.name);

    const pRecipientType = escapeHtml(recipient.type);

    const pRegistrationNo = escapeHtml(recipient.registrationNo || "—");

    const pFatherName = escapeHtml(recipient.fatherName || "—");

    const pMobile = escapeHtml(recipient.mobile || "—");

    const pBatch = escapeHtml(fee?.batch?.title || "—");

    const pKitName = escapeHtml(fee?.kit?.name || "Distribution Kit");

    const pItemName = escapeHtml(fee?.item?.name || "Complete Kit");

    const pQuantity = escapeHtml(fee?.quantity || 0);

    const pAssignmentId = escapeHtml(fee?.id || "—");

    const pPaymentMethod = escapeHtml(paymentMethod);

    const pAccountNo = escapeHtml(transaction?.account_no || "—");

    const pTransactionNo = escapeHtml(transaction?.transaction_no || "—");

    const pRemark = escapeHtml(transaction?.remark || "");

    const pPaymentDate = escapeHtml(formatDate(transaction?.payment_date));

    const pCreatedAt = escapeHtml(formatDateTime(transaction?.created_at));

    const pKitAmount = formatAmount(kitAmount);

    const pDiscountAmount = formatAmount(discountAmount);

    const pFinalAmount = formatAmount(finalAmount);

    const pPreviousPaidAmount = formatAmount(previousPaidAmount);

    const pCurrentPayment = formatAmount(currentPayment);

    const pTotalPaid = formatAmount(totalPaid);

    const pDueAmount = formatAmount(dueAmount);

    const amountWords = escapeHtml(
      `${convertNumberToWords(currentPayment)} Rupees Only`,
    );

    /* ==========================================================
       HEADER
    ========================================================== */

    const receiptHeader = headerLogoUrl
      ? `
          <div class="header-image-wrapper">
            <img
              src="${headerLogoUrl}"
              class="header-image"
              alt="Receipt Header"
            />
          </div>
        `
      : `
          <div class="school-header">

            ${
              logoUrl
                ? `
                  <div class="logo-wrapper">
                    <img
                      src="${logoUrl}"
                      class="school-logo"
                      alt="School Logo"
                    />
                  </div>
                `
                : ""
            }

            <div class="school-information">

              <div class="school-name">
                ${pSchoolName}
              </div>

              ${
                pSchoolAddress
                  ? `
                    <div class="school-address">
                      ${pSchoolAddress}
                    </div>
                  `
                  : ""
              }

              ${
                pSchoolMobile
                  ? `
                    <div class="school-contact">
                      Mobile No : ${pSchoolMobile}
                    </div>
                  `
                  : ""
              }

              ${
                pSchoolEmail
                  ? `
                    <div class="school-contact">
                      Email : ${pSchoolEmail}
                    </div>
                  `
                  : ""
              }

            </div>

          </div>
        `;

    /* ==========================================================
       SIGNATURE HTML
    ========================================================== */

    const authorizedSignatureHtml =
      showSignature && signatureUrl
        ? `
          <img
            src="${signatureUrl}"
            class="signature-image"
            alt="Authorized Signature"
          />
        `
        : `
          <div class="signature-space"></div>
        `;

    /* ==========================================================
       PRINT HTML
    ========================================================== */

    const printHtml = `
      <!DOCTYPE html>

      <html>

      <head>

        <meta charset="UTF-8" />

        <title>
          Payment Receipt - ${pReceiptNo}
        </title>

        <style>

          @page {
            size: A4;
            margin: 7mm;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #fff;
          }

          body {
            font-family:
              Arial,
              Helvetica,
              sans-serif;

            color: #222;
            font-size: 10.5px;
            line-height: 1.35;
          }

          .receipt {
            width: 100%;
            max-width: 194mm;
            margin: 0 auto;
            padding: 1mm 4mm;
            background: #fff;
          }

          /* =====================================================
             SCHOOL HEADER
          ====================================================== */

          .school-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 14px;
            padding-bottom: 7px;
          }

          .logo-wrapper {
            width: 72px;
            min-width: 72px;
            text-align: center;
          }

          .school-logo {
            width: 68px;
            height: 60px;
            object-fit: contain;
          }

          .school-information {
            flex: 1;
            text-align: center;
          }

          .school-name {
            font-size: 18px;
            font-weight: 800;
            text-transform: uppercase;
            margin-bottom: 2px;
            letter-spacing: 0.2px;
          }

          .school-address,
          .school-contact {
            font-size: 8.5px;
            margin-top: 1px;
          }

          .header-image-wrapper {
            width: 100%;
            text-align: center;
            padding-bottom: 6px;
          }

          .header-image {
            max-width: 100%;
            max-height: 29mm;
            object-fit: contain;
          }

          /* =====================================================
             RECEIPT TITLE
          ====================================================== */

          .header-divider {
            border-top: 1px solid #222;
          }

          .receipt-heading {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            min-height: 34px;
            border-bottom: 1px solid #222;
          }

          .heading-left {
            text-align: left;
            font-weight: 700;
            font-size: 9.5px;
          }

          .heading-center {
            text-align: center;
          }

          .receipt-badge {
            display: inline-block;
            font-weight: 800;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }

          .heading-right {
            text-align: right;
            font-weight: 700;
            font-size: 9.5px;
          }

          /* =====================================================
             RECEIPT META
          ====================================================== */

          .meta-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 7px;
          }

          .meta-table td {
            padding: 4px 3px;
            border-bottom: 1px solid #ddd;
          }

          .meta-label {
            width: 16%;
            color: #666;
            font-weight: 600;
            font-size: 8.5px;
          }

          .meta-value {
            width: 34%;
            font-weight: 600;
          }

          /* =====================================================
             SECTIONS
          ====================================================== */

          .section {
            margin-top: 8px;
          }

          .section-title {
            padding: 4px 0;
            margin-bottom: 3px;
            border-bottom: 1px solid #ccc;
            font-weight: 800;
            font-size: 9.5px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            color: #333;
          }

          .section-body {
            padding: 1px 0;
          }

          /* =====================================================
             DETAILS
          ====================================================== */

          .details-table {
            width: 100%;
            border-collapse: collapse;
          }

          .details-table td {
            padding: 3.5px 2px;
            vertical-align: top;
          }

          .details-label {
            width: 18%;
            color: #666;
            font-size: 8.5px;
          }

          .details-colon {
            width: 2%;
            color: #999;
          }

          .details-value {
            width: 30%;
            font-weight: 600;
          }

          /* =====================================================
             PAYMENT TABLE
          ====================================================== */

          .payment-table {
            width: 100%;
            border-collapse: collapse;
          }

          .payment-table th,
          .payment-table td {
            padding: 4px 3px;
            border-bottom: 1px solid #e5e5e5;
          }

          .payment-table th {
            font-size: 8.5px;
            font-weight: 700;
            text-align: left;
            border-bottom: 1px solid #555;
          }

          .payment-table .amount {
            text-align: right;
            white-space: nowrap;
          }

          .payment-table .total-row td {
            font-weight: 700;
            border-top: 1px solid #555;
          }

          .payment-table .current-payment td {
            font-weight: 700;
          }

          .payment-table .due-row td {
            font-weight: 700;
          }

          /* =====================================================
             AMOUNT RECEIVED
          ====================================================== */

          .amount-box {
            margin-top: 9px;
            padding: 7px 0;
            border-top: 1px solid #555;
            border-bottom: 1px solid #555;
          }

          .amount-box-table {
            width: 100%;
            border-collapse: collapse;
          }

          .amount-box-table td {
            vertical-align: middle;
          }

          .amount-label {
            font-size: 8.5px;
            font-weight: 700;
            text-transform: uppercase;
          }

          .amount-words {
            font-size: 9.5px;
            font-weight: 600;
            margin-top: 2px;
          }

          .amount-number {
            text-align: right;
            font-size: 20px;
            font-weight: 800;
          }

          /* =====================================================
             PAYMENT INFORMATION
          ====================================================== */

          .payment-info-table {
            width: 100%;
            border-collapse: collapse;
          }

          .payment-info-table td {
            padding: 3.5px 2px;
            vertical-align: top;
          }

          .payment-info-label {
            width: 18%;
            color: #666;
            font-size: 8.5px;
          }

          .payment-info-value {
            width: 32%;
            font-weight: 600;
          }

          /* =====================================================
             STATUS
          ====================================================== */

          .status-paid {
            color: #198754;
            font-weight: 800;
            font-size: 8.5px;
          }

          .status-partial {
            color: #d97706;
            font-weight: 800;
            font-size: 8.5px;
          }

          /* =====================================================
             REMARK
          ====================================================== */

          .remark {
            margin-top: 6px;
            padding: 4px 0;
            border-top: 1px solid #ddd;
            font-size: 8.5px;
          }

          .remark-label {
            font-weight: 700;
          }

          /* =====================================================
             SIGNATURE
          ====================================================== */

          .signature-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 27px;
          }

          .signature-table td {
            width: 50%;
            text-align: center;
            vertical-align: bottom;
          }

          .signature-image {
            width: 100px;
            height: 42px;
            object-fit: contain;
            display: block;
            margin: 0 auto 2px;
          }

          .signature-space {
            height: 42px;
          }

          .signature-label {
            font-size: 8.5px;
            font-weight: 700;
          }

          /* =====================================================
             FOOTER
          ====================================================== */

          .receipt-footer {
            border-top: 1px solid #aaa;
            margin-top: 11px;
            padding-top: 5px;
            text-align: center;
            font-size: 7.5px;
            color: #666;
          }

          .no-break {
            page-break-inside: avoid;
          }

          @media print {

            html,
            body {
              width: 100%;
              background: #fff;
            }

            .receipt {
              width: 100%;
              max-width: none;
              padding: 0;
            }

          }

        </style>

      </head>

      <body>

        <div class="receipt">

          ${receiptHeader}

          <div class="header-divider"></div>

          <!-- RECEIPT TITLE -->

          <div class="receipt-heading">

            <div class="heading-left">
              ${pRecipientType} Details
            </div>

            <div class="heading-center">
              <span class="receipt-badge">
                Fee Receipt
              </span>
            </div>

            <div class="heading-right">
              Student Copy
            </div>

          </div>

          <!-- RECEIPT META -->

          <table class="meta-table">

            <tr>

              <td class="meta-label">
                Receipt No
              </td>

              <td class="meta-value">
                ${pReceiptNo}
              </td>

              <td class="meta-label">
                Date
              </td>

              <td class="meta-value">
                ${pPaymentDate}
              </td>

            </tr>

            <tr>

              <td class="meta-label">
                Payment Status
              </td>

              <td class="meta-value">

                ${
                  isFullyPaid
                    ? `
                      <span class="status-paid">
                        PAID
                      </span>
                    `
                    : `
                      <span class="status-partial">
                        PARTIAL PAYMENT
                      </span>
                    `
                }

              </td>

              <td class="meta-label">
                Transaction ID
              </td>

              <td class="meta-value">
                ${escapeHtml(transactionId)}
              </td>

            </tr>

          </table>

          <!-- RECIPIENT DETAILS -->

          <div class="section no-break">

            <div class="section-title">
              ${pRecipientType} Details
            </div>

            <div class="section-body">

              <table class="details-table">

                <tr>

                  <td class="details-label">
                    ${pRecipientType} Name
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pRecipientName}
                  </td>

                  <td class="details-label">

                    ${
                      recipient.type === "Student"
                        ? "Registration No"
                        : "Mobile No"
                    }

                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">

                    ${recipient.type === "Student" ? pRegistrationNo : pMobile}

                  </td>

                </tr>

                ${
                  recipient.type === "Student"
                    ? `
                      <tr>

                        <td class="details-label">
                          Father's Name
                        </td>

                        <td class="details-colon">
                          :
                        </td>

                        <td class="details-value">
                          ${pFatherName}
                        </td>

                        <td class="details-label">
                          Mobile No
                        </td>

                        <td class="details-colon">
                          :
                        </td>

                        <td class="details-value">
                          ${pMobile}
                        </td>

                      </tr>
                    `
                    : ""
                }

                <tr>

                  <td class="details-label">
                    Batch
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pBatch}
                  </td>

                  <td class="details-label">
                    Quantity
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pQuantity}
                  </td>

                </tr>

              </table>

            </div>

          </div>

          <!-- DISTRIBUTION KIT -->

          <div class="section no-break">

            <div class="section-title">
              Distribution Kit Details
            </div>

            <div class="section-body">

              <table class="details-table">

                <tr>

                  <td class="details-label">
                    Kit Name
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pKitName}
                  </td>

                  <td class="details-label">
                    Item
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pItemName}
                  </td>

                </tr>

                <tr>

                  <td class="details-label">
                    Assignment ID
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    #${pAssignmentId}
                  </td>

                  <td class="details-label">
                    Quantity
                  </td>

                  <td class="details-colon">
                    :
                  </td>

                  <td class="details-value">
                    ${pQuantity}
                  </td>

                </tr>

              </table>

            </div>

          </div>

          <!-- PAYMENT BREAKDOWN -->

          <div class="section no-break">

            <div class="section-title">
              Payment Details
            </div>

            <div class="section-body">

              <table class="payment-table">

                <thead>

                  <tr>

                    <th style="width: 8%;">
                      #
                    </th>

                    <th>
                      Description
                    </th>

                    <th
                      style="width: 25%;"
                      class="amount"
                    >
                      Amount
                    </th>

                  </tr>

                </thead>

                <tbody>

                  <tr>

                    <td>
                      1
                    </td>

                    <td>
                      Distribution Kit Amount
                    </td>

                    <td class="amount">
                      ${pKitAmount}
                    </td>

                  </tr>

                  ${
                    discountAmount > 0
                      ? `
                        <tr>

                          <td>
                            2
                          </td>

                          <td>
                            Discount
                          </td>

                          <td
                            class="amount"
                            style="color:#dc3545;"
                          >
                            -${pDiscountAmount}
                          </td>

                        </tr>
                      `
                      : ""
                  }

                  <tr class="total-row">

                    <td
                      colspan="2"
                      style="text-align:right;"
                    >
                      Final Amount
                    </td>

                    <td class="amount">
                      ${pFinalAmount}
                    </td>

                  </tr>

                  <tr>

                    <td
                      colspan="2"
                      style="text-align:right;"
                    >
                      Previous Paid
                    </td>

                    <td class="amount">
                      ${pPreviousPaidAmount}
                    </td>

                  </tr>

                  <tr class="current-payment">

                    <td
                      colspan="2"
                      style="text-align:right;"
                    >
                      Current Payment
                    </td>

                    <td
                      class="amount"
                      style="color:#198754;"
                    >
                      ${pCurrentPayment}
                    </td>

                  </tr>

                  <tr class="total-row">

                    <td
                      colspan="2"
                      style="text-align:right;"
                    >
                      Total Paid
                    </td>

                    <td class="amount">
                      ${pTotalPaid}
                    </td>

                  </tr>

                  <tr class="due-row">

                    <td
                      colspan="2"
                      style="text-align:right;"
                    >
                      Balance Due
                    </td>

                    <td
                      class="amount"
                      style="color:${dueAmount > 0 ? "#dc3545" : "#198754"};"
                    >
                      ${pDueAmount}
                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

          </div>

          <!-- AMOUNT RECEIVED -->

          <div class="amount-box no-break">

            <table class="amount-box-table">

              <tr>

                <td>

                  <div class="amount-label">
                    Amount Received
                  </div>

                  <div class="amount-words">
                    ${amountWords}
                  </div>

                </td>

                <td class="amount-number">
                  ${pCurrentPayment}
                </td>

              </tr>

            </table>

          </div>

          <!-- PAYMENT INFORMATION -->

          <div class="section no-break">

            <div class="section-title">
              Payment Information
            </div>

            <div class="section-body">

              <table class="payment-info-table">

                <tr>

                  <td class="payment-info-label">
                    Payment Method
                  </td>

                  <td class="payment-info-value">
                    ${pPaymentMethod}
                  </td>

                  <td class="payment-info-label">
                    Payment Date
                  </td>

                  <td class="payment-info-value">
                    ${pPaymentDate}
                  </td>

                </tr>

                ${
                  transaction?.account_no || transaction?.transaction_no
                    ? `
                      <tr>

                        <td class="payment-info-label">
                          Account No
                        </td>

                        <td class="payment-info-value">
                          ${pAccountNo}
                        </td>

                        <td class="payment-info-label">
                          Transaction No
                        </td>

                        <td class="payment-info-value">
                          ${pTransactionNo}
                        </td>

                      </tr>
                    `
                    : ""
                }

                <tr>

                  <td class="payment-info-label">
                    Receipt No
                  </td>

                  <td class="payment-info-value">
                    ${pReceiptNo}
                  </td>

                  <td class="payment-info-label">
                    Recorded At
                  </td>

                  <td class="payment-info-value">
                    ${pCreatedAt}
                  </td>

                </tr>

              </table>

            </div>

          </div>

          ${
            transaction?.remark
              ? `
                <div class="remark no-break">

                  <span class="remark-label">
                    Remark:
                  </span>

                  ${pRemark}

                </div>
              `
              : ""
          }

          <!-- SIGNATURES -->

          <table class="signature-table">

            <tr>

              <td>

                <div class="signature-space"></div>

                <div class="signature-label">
                  Recipient Signature
                </div>

              </td>

              <td>

                ${authorizedSignatureHtml}

                <div class="signature-label">
                  Auth. Signature
                </div>

              </td>

            </tr>

          </table>

          <!-- FOOTER -->

          <div class="receipt-footer">

            <div>
              Fees once paid are non-refundable.
            </div>

            <div style="margin-top:2px;">
              This is a computer generated receipt.
            </div>

          </div>

        </div>

      </body>

      </html>
    `;

    /* ==========================================================
       WRITE PRINT WINDOW
    ========================================================== */

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();

    /* ==========================================================
       WAIT FOR IMAGES
    ========================================================== */

    const waitForImages = () => {
      const images = Array.from(printWindow.document.images);

      if (!images.length) {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 300);

        return;
      }

      let remaining = images.length;

      let printed = false;

      const startPrint = () => {
        if (printed) {
          return;
        }

        remaining--;

        if (remaining <= 0) {
          printed = true;

          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
          }, 500);
        }
      };

      images.forEach((image) => {
        if (image.complete) {
          startPrint();
        } else {
          image.onload = startPrint;
          image.onerror = startPrint;
        }
      });

      /*
       * Safety fallback.
       * Prevents print from getting stuck if an image
       * never responds.
       */
      setTimeout(() => {
        if (!printed) {
          printed = true;

          printWindow.focus();
          printWindow.print();
        }
      }, 4000);
    };

    setTimeout(waitForImages, 200);

    printWindow.onafterprint = () => {
      setTimeout(() => {
        printWindow.close();
      }, 200);
    };
  };

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              <DistributionKitLayout />

              {/* ==================================================
                  PAGE HEADER
              ================================================== */}

              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle bg-label-success d-flex align-items-center justify-content-center"
                        style={{
                          width: 50,
                          height: 50,
                        }}
                      >
                        <i className="ri-checkbox-circle-line fs-3 text-success" />
                      </div>

                      <div>
                        <h4 className="mb-1 fw-bold">Payment Receipt</h4>

                        <div className="text-muted">{receiptNo}</div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-label-secondary"
                        onClick={() => navigate("/distribution-kits/report")}
                      >
                        <i className="ri-arrow-left-line me-1" />
                        Back to Report
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={printReceipt}
                      >
                        <i className="ri-printer-line me-1" />
                        Print Receipt
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ==================================================
                  RECEIPT PREVIEW
              ================================================== */}

              <div
                style={{
                  background: "#f4f5f7",
                  padding: "24px 12px",
                  borderRadius: "8px",
                }}
              >
                <div
                  id="distribution-kit-payment-receipt"
                  className="bg-white mx-auto"
                  style={{
                    maxWidth: "850px",
                    boxShadow: "0 2px 14px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      padding: "28px 36px 24px",
                    }}
                  >
                    {/* ==================================================
                        SCHOOL HEADER
                    ================================================== */}

                    {headerLogoUrl ? (
                      <div
                        className="text-center"
                        style={{
                          paddingBottom: 10,
                        }}
                      >
                        <img
                          src={headerLogoUrl}
                          alt="Receipt Header"
                          style={{
                            width: "100%",
                            maxHeight: "145px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          gap: 18,
                          paddingBottom: 10,
                        }}
                      >
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt="School Logo"
                            style={{
                              width: 95,
                              height: 75,
                              objectFit: "contain",
                            }}
                          />
                        )}

                        <div
                          className="text-center"
                          style={{
                            flex: 1,
                          }}
                        >
                          <div
                            className="fw-bold text-uppercase"
                            style={{
                              fontSize: 22,
                              letterSpacing: "0.3px",
                            }}
                          >
                            {schoolName}
                          </div>

                          {schoolAddress && (
                            <div
                              className="small mt-1"
                              style={{
                                color: "#555",
                              }}
                            >
                              {schoolAddress}
                            </div>
                          )}

                          {schoolMobile && (
                            <div
                              className="small mt-1"
                              style={{
                                color: "#555",
                              }}
                            >
                              Mobile No : {schoolMobile}
                            </div>
                          )}

                          {schoolEmail && (
                            <div
                              className="small"
                              style={{
                                color: "#555",
                              }}
                            >
                              Email : {schoolEmail}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        borderTop: "1px solid #222",
                      }}
                    />

                    {/* ==================================================
                        RECEIPT TITLE
                    ================================================== */}

                    <div
                      className="row align-items-center text-center"
                      style={{
                        minHeight: 48,
                        borderBottom: "1px solid #222",
                      }}
                    >
                      <div className="col-4 text-start">
                        <div
                          className="fw-bold"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {recipient.type} Details
                        </div>
                      </div>

                      <div className="col-4">
                        <div
                          className="fw-bold"
                          style={{
                            fontSize: 16,
                            letterSpacing: "0.6px",
                          }}
                        >
                          FEE RECEIPT
                        </div>

                        <div
                          className="text-muted"
                          style={{
                            fontSize: 9,
                          }}
                        >
                          Distribution Kit Payment
                        </div>
                      </div>

                      <div className="col-4 text-end">
                        <div
                          className="fw-bold"
                          style={{
                            fontSize: 11,
                          }}
                        >
                          Student Copy
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        RECEIPT META
                    ================================================== */}

                    <div
                      className="row g-0"
                      style={{
                        marginTop: 10,
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <div className="col-3 py-2">
                        <div
                          className="text-muted"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Receipt No
                        </div>

                        <div
                          className="fw-bold"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {receiptNo}
                        </div>
                      </div>

                      <div className="col-3 py-2">
                        <div
                          className="text-muted"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Payment Date
                        </div>

                        <div
                          className="fw-semibold"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {formatDate(transaction?.payment_date)}
                        </div>
                      </div>

                      <div className="col-3 py-2">
                        <div
                          className="text-muted"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Transaction ID
                        </div>

                        <div
                          className="fw-semibold"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          #{transactionId}
                        </div>
                      </div>

                      <div className="col-3 py-2 text-end">
                        <div
                          className="text-muted"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Status
                        </div>

                        <div
                          className={
                            isFullyPaid
                              ? "fw-bold text-success"
                              : "fw-bold text-warning"
                          }
                          style={{
                            fontSize: 11,
                          }}
                        >
                          {isFullyPaid ? "PAID" : "PARTIAL PAYMENT"}
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        RECIPIENT DETAILS
                    ================================================== */}

                    <div
                      style={{
                        marginTop: 14,
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: 11,
                          paddingBottom: 5,
                          borderBottom: "1px solid #ccc",
                        }}
                      >
                        {recipient.type} Details
                      </div>

                      <div
                        className="row"
                        style={{
                          paddingTop: 5,
                        }}
                      >
                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            {recipient.type} Name
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {recipient.name}
                          </div>
                        </div>

                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            {recipient.type === "Student"
                              ? "Registration No"
                              : "Mobile No"}
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {recipient.type === "Student"
                              ? recipient.registrationNo || "—"
                              : recipient.mobile || "—"}
                          </div>
                        </div>

                        {recipient.type === "Student" && (
                          <>
                            <div className="col-6 mb-2">
                              <span
                                className="text-muted"
                                style={{
                                  fontSize: 10,
                                }}
                              >
                                Father's Name
                              </span>

                              <div
                                className="fw-semibold"
                                style={{
                                  fontSize: 12,
                                }}
                              >
                                {recipient.fatherName || "—"}
                              </div>
                            </div>

                            <div className="col-6 mb-2">
                              <span
                                className="text-muted"
                                style={{
                                  fontSize: 10,
                                }}
                              >
                                Mobile No
                              </span>

                              <div
                                className="fw-semibold"
                                style={{
                                  fontSize: 12,
                                }}
                              >
                                {recipient.mobile || "—"}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Batch
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {fee?.batch?.title || "—"}
                          </div>
                        </div>

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Quantity
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {fee?.quantity || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        KIT DETAILS
                    ================================================== */}

                    <div
                      style={{
                        marginTop: 12,
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: 11,
                          paddingBottom: 5,
                          borderBottom: "1px solid #ccc",
                        }}
                      >
                        Distribution Kit Details
                      </div>

                      <div
                        className="row"
                        style={{
                          paddingTop: 5,
                        }}
                      >
                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Kit Name
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {fee?.kit?.name || "Distribution Kit"}
                          </div>
                        </div>

                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Item
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {fee?.item?.name || "Complete Kit"}
                          </div>
                        </div>

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Assignment ID
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            #{fee?.id || "—"}
                          </div>
                        </div>

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Quantity
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {fee?.quantity || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        PAYMENT DETAILS
                    ================================================== */}

                    <div
                      style={{
                        marginTop: 14,
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: 11,
                          paddingBottom: 5,
                          borderBottom: "1px solid #ccc",
                        }}
                      >
                        Payment Details
                      </div>

                      <table
                        className="table mb-0"
                        style={{
                          fontSize: 11,
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                borderBottom: "1px solid #555",
                                padding: "5px 3px",
                              }}
                            >
                              #
                            </th>

                            <th
                              style={{
                                borderBottom: "1px solid #555",
                                padding: "5px 3px",
                              }}
                            >
                              Description
                            </th>

                            <th
                              className="text-end"
                              style={{
                                borderBottom: "1px solid #555",
                                padding: "5px 3px",
                              }}
                            >
                              Amount
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            <td
                              style={{
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              1
                            </td>

                            <td
                              style={{
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              Distribution Kit Amount
                            </td>

                            <td
                              className="text-end"
                              style={{
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {formatAmount(kitAmount)}
                            </td>
                          </tr>

                          {discountAmount > 0 && (
                            <tr>
                              <td />

                              <td className="text-end">Discount</td>

                              <td className="text-end text-danger">
                                -{formatAmount(discountAmount)}
                              </td>
                            </tr>
                          )}

                          <tr>
                            <td
                              colSpan={2}
                              className="text-end fw-bold"
                              style={{
                                borderTop: "1px solid #555",
                              }}
                            >
                              Final Amount
                            </td>

                            <td
                              className="text-end fw-bold"
                              style={{
                                borderTop: "1px solid #555",
                              }}
                            >
                              {formatAmount(finalAmount)}
                            </td>
                          </tr>

                          <tr>
                            <td colSpan={2} className="text-end">
                              Previous Paid
                            </td>

                            <td className="text-end">
                              {formatAmount(previousPaidAmount)}
                            </td>
                          </tr>

                          <tr>
                            <td colSpan={2} className="text-end fw-bold">
                              Current Payment
                            </td>

                            <td className="text-end fw-bold text-success">
                              {formatAmount(currentPayment)}
                            </td>
                          </tr>

                          <tr>
                            <td
                              colSpan={2}
                              className="text-end fw-bold"
                              style={{
                                borderTop: "1px solid #555",
                              }}
                            >
                              Total Paid
                            </td>

                            <td
                              className="text-end fw-bold"
                              style={{
                                borderTop: "1px solid #555",
                              }}
                            >
                              {formatAmount(totalPaid)}
                            </td>
                          </tr>

                          <tr>
                            <td colSpan={2} className="text-end fw-bold">
                              Balance Due
                            </td>

                            <td
                              className={`text-end fw-bold ${
                                dueAmount > 0 ? "text-danger" : "text-success"
                              }`}
                            >
                              {formatAmount(dueAmount)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* ==================================================
                        AMOUNT RECEIVED
                    ================================================== */}

                    <div
                      style={{
                        marginTop: 12,
                        padding: "10px 0",
                        borderTop: "1px solid #555",
                        borderBottom: "1px solid #555",
                      }}
                    >
                      <div className="row align-items-center">
                        <div className="col-7">
                          <div
                            className="text-uppercase fw-bold"
                            style={{
                              fontSize: 9,
                            }}
                          >
                            Amount Received
                          </div>

                          <div
                            className="fw-semibold text-muted"
                            style={{
                              fontSize: 10,
                              marginTop: 2,
                            }}
                          >
                            {convertNumberToWords(currentPayment)} Rupees Only
                          </div>
                        </div>

                        <div
                          className="col-5 text-end fw-bold text-success"
                          style={{
                            fontSize: 22,
                          }}
                        >
                          {formatAmount(currentPayment)}
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        PAYMENT INFORMATION
                    ================================================== */}

                    <div
                      style={{
                        marginTop: 12,
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: 11,
                          paddingBottom: 5,
                          borderBottom: "1px solid #ccc",
                        }}
                      >
                        Payment Information
                      </div>

                      <div
                        className="row"
                        style={{
                          paddingTop: 5,
                        }}
                      >
                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Payment Method
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {paymentMethod}
                          </div>
                        </div>

                        <div className="col-6 mb-2">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Payment Date
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {formatDate(transaction?.payment_date)}
                          </div>
                        </div>

                        {transaction?.account_no && (
                          <div className="col-6 mb-2">
                            <span
                              className="text-muted"
                              style={{
                                fontSize: 10,
                              }}
                            >
                              Account No
                            </span>

                            <div
                              className="fw-semibold"
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {transaction.account_no}
                            </div>
                          </div>
                        )}

                        {transaction?.transaction_no && (
                          <div className="col-6 mb-2">
                            <span
                              className="text-muted"
                              style={{
                                fontSize: 10,
                              }}
                            >
                              Transaction No
                            </span>

                            <div
                              className="fw-semibold"
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {transaction.transaction_no}
                            </div>
                          </div>
                        )}

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Receipt No
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {receiptNo}
                          </div>
                        </div>

                        <div className="col-6">
                          <span
                            className="text-muted"
                            style={{
                              fontSize: 10,
                            }}
                          >
                            Recorded At
                          </span>

                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            {formatDateTime(transaction?.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        REMARK
                    ================================================== */}

                    {transaction?.remark && (
                      <div
                        style={{
                          marginTop: 10,
                          paddingTop: 7,
                          borderTop: "1px solid #ddd",
                        }}
                      >
                        <span
                          className="fw-bold"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Remark:
                        </span>

                        <span
                          className="ms-1"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          {transaction.remark}
                        </span>
                      </div>
                    )}

                    {/* ==================================================
                        SIGNATURES
                    ================================================== */}

                    <div
                      className="row"
                      style={{
                        marginTop: 42,
                      }}
                    >
                      <div className="col-6 text-center">
                        <div
                          style={{
                            height: 45,
                          }}
                        />

                        <div
                          className="fw-semibold"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Recipient Signature
                        </div>
                      </div>

                      <div className="col-6 text-center">
                        {showSignature && signatureUrl ? (
                          <img
                            src={signatureUrl}
                            alt="Authorized Signature"
                            style={{
                              width: 110,
                              height: 45,
                              objectFit: "contain",
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: 45,
                            }}
                          />
                        )}

                        <div
                          className="fw-semibold"
                          style={{
                            fontSize: 10,
                          }}
                        >
                          Auth. Signature
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        FOOTER
                    ================================================== */}

                    <div
                      className="text-center"
                      style={{
                        marginTop: 18,
                        paddingTop: 7,
                        borderTop: "1px solid #aaa",
                      }}
                    >
                      <div
                        className="text-danger"
                        style={{
                          fontSize: 9,
                        }}
                      >
                        Fees once paid are non-refundable.
                      </div>

                      <div
                        className="text-muted"
                        style={{
                          fontSize: 8,
                          marginTop: 2,
                        }}
                      >
                        This is a computer generated receipt.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ==================================================
                  BOTTOM ACTIONS
              ================================================== */}

              <div className="d-flex justify-content-between mt-4">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  onClick={() => navigate("/distribution-kits/report")}
                >
                  <i className="ri-arrow-left-line me-1" />
                  Back to Report
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={printReceipt}
                >
                  <i className="ri-printer-line me-1" />
                  Print Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
